import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { User } from 'types'

// Enable custom format parsing for dayjs
dayjs.extend(customParseFormat)

export const customSortOperator = (
  v1: string | undefined | null,
  v2: string | undefined | null,
  cellParams1?: { api?: any; field?: string },
  _cellParams2?: unknown
): number => {
  const a = v1?.trim() || '' // Normalize and trim
  const b = v2?.trim() || ''

  // If both are empty, they are equal
  if (!a && !b) return 0

  // Get current sort direction from the grid API
  let sortDirection: 'asc' | 'desc' | null = 'asc'
  try {
    const api = cellParams1?.api
    const field = cellParams1?.field
    if (api && field) {
      const sortModel = api.getSortModel?.() ?? api.state?.sorting?.sortModel
      if (Array.isArray(sortModel)) {
        const fieldSort = sortModel.find((s: any) => s.field === field)
        if (fieldSort?.sort) {
          sortDirection = fieldSort.sort
        }
      }
    }
  } catch (e) {
    sortDirection = 'asc'
  }

  // Empty values should always be at the bottom regardless of sort direction
  if (!a) return sortDirection === 'desc' ? -1 : 1
  if (!b) return sortDirection === 'desc' ? 1 : -1

  // Regular string comparison
  return a.localeCompare(b)
}
export const customNumericSortOperator = (
  v1: number | string | null | undefined,
  v2: number | string | null | undefined,
  cellParams1?: { api?: any; field?: string },
  _cellParams2?: unknown
): number => {
  const isEmpty1 = v1 === null || v1 === undefined || v1 === ''
  const isEmpty2 = v2 === null || v2 === undefined || v2 === ''

  // If both are empty, they are equal
  if (isEmpty1 && isEmpty2) return 0

  // Get current sort direction from the grid API
  let sortDirection: 'asc' | 'desc' | null = 'asc'
  try {
    const api = cellParams1?.api
    const field = cellParams1?.field
    if (api && field) {
      const sortModel = api.getSortModel?.() ?? api.state?.sorting?.sortModel
      if (Array.isArray(sortModel)) {
        const fieldSort = sortModel.find((s: any) => s.field === field)
        if (fieldSort?.sort) {
          sortDirection = fieldSort.sort
        }
      }
    }
  } catch (e) {
    sortDirection = 'asc'
  }

  // Empty values should always be at the bottom regardless of sort direction
  if (isEmpty1) return sortDirection === 'desc' ? -1 : 1
  if (isEmpty2) return sortDirection === 'desc' ? 1 : -1

  const num1 = typeof v1 === 'string' ? parseFloat(v1.replace(/,/g, '')) : Number(v1)
  const num2 = typeof v2 === 'string' ? parseFloat(v2.replace(/,/g, '')) : Number(v2)

  return num1 - num2
}

export const customDateSortOperator = (
  v1: string | null | undefined,
  v2: string | null | undefined,
  cellParams1?: { api?: any; field?: string },
  _cellParams2?: unknown
): number => {
  const isEmpty1 = !v1
  const isEmpty2 = !v2

  // If both are empty, they are equal
  if (isEmpty1 && isEmpty2) return 0

  // Get current sort direction from the grid API
  // Use multiple fallback methods to ensure we get the correct direction
  let sortDirection: 'asc' | 'desc' | null = 'asc'
  try {
    const api = cellParams1?.api
    const field = cellParams1?.field
    if (api && field) {
      // Try getSortModel first
      const sortModel = api.getSortModel?.() ?? api.state?.sorting?.sortModel
      if (Array.isArray(sortModel)) {
        const fieldSort = sortModel.find((s: any) => s.field === field)
        if (fieldSort?.sort) {
          sortDirection = fieldSort.sort
        }
      }
    }
  } catch (e) {
    // Default to asc if we can't get sort direction
    sortDirection = 'asc'
  }

  // Empty values should always be at the bottom regardless of sort direction
  // For ASC: return positive to push empty after non-empty
  // For DESC: return negative so after grid reversal, empty is still at bottom
  if (isEmpty1) return sortDirection === 'desc' ? -1 : 1
  if (isEmpty2) return sortDirection === 'desc' ? 1 : -1

  // Parse dates using dayjs which handles more formats reliably
  // Supports: ISO, 'MM/DD/YYYY', 'MM/DD/YYYY hh:mm A', timestamps, etc.
  const parseDate = (val: string): number => {
    // Try parsing with dayjs (handles ISO and many common formats)
    const parsed = dayjs(val)
    if (parsed.isValid()) {
      return parsed.valueOf()
    }

    // Try explicit formats if auto-parse fails
    const formats = ['MM/DD/YYYY hh:mm A', 'MM/DD/YYYY HH:mm:ss', 'MM/DD/YYYY', 'YYYY-MM-DD']
    for (const format of formats) {
      const explicit = dayjs(val, format, true)
      if (explicit.isValid()) {
        return explicit.valueOf()
      }
    }

    return -Infinity
  }

  const date1 = parseDate(v1)
  const date2 = parseDate(v2)

  return date1 - date2
}

export const customTrackingSortOperator = (
  v1: any,
  v2: any,
  cellParams1?: { api?: any; field?: string },
  _cellParams2?: unknown
): number => {
  const a = typeof v1 === 'string' ? v1.trim().toLowerCase() : ''
  const b = typeof v2 === 'string' ? v2.trim().toLowerCase() : ''

  // If both are empty, they are equal
  if (!a && !b) return 0

  // Get current sort direction from the grid API
  let sortDirection: 'asc' | 'desc' | null = 'asc'
  try {
    const api = cellParams1?.api
    const field = cellParams1?.field
    if (api && field) {
      const sortModel = api.getSortModel?.() ?? api.state?.sorting?.sortModel
      if (Array.isArray(sortModel)) {
        const fieldSort = sortModel.find((s: any) => s.field === field)
        if (fieldSort?.sort) {
          sortDirection = fieldSort.sort
        }
      }
    }
  } catch (e) {
    sortDirection = 'asc'
  }

  // Empty values should always be at the bottom regardless of sort direction
  if (!a) return sortDirection === 'desc' ? -1 : 1
  if (!b) return sortDirection === 'desc' ? 1 : -1

  // Regular string comparison
  return a.localeCompare(b)
}
export const customBooleanSortOperator = (
  v1: boolean | string | null | undefined,
  v2: boolean | string | null | undefined
): number => {
  // Normalize to actual boolean
  const a = typeof v1 === 'string' ? v1.toLowerCase() === 'true' : Boolean(v1)
  const b = typeof v2 === 'string' ? v2.toLowerCase() === 'true' : Boolean(v2)

  // // false first, true last (so "unchecked" sorts above "checked")
  // if (a === b) return 0
  // return a ? 1 : -1
  // true first, false last (so "checked" sorts above "unchecked")
  if (a === b) return 0
  return a ? 1 : -1
}
export const customSortUsersOperator = (users: User[]) => {
  // Sort by firstName, and if they are the same, sort by lastName
  return users.sort((a, b) => {
    const comp1 = a?.firstName?.localeCompare(b?.firstName)
    if (comp1 !== 0) {
      return comp1 // Sort by first name
    }
    return a?.lastName?.localeCompare(b?.lastName) // If first names are the same, sort by last name
  })
}
